import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import styles from "./piechart.module.css";

type DataItem = {
  name: string;
  value: number;
};

type PieArcDatum = d3.PieArcDatum<DataItem>;

type PieChartProps = {
  data: DataItem[];
  title: string;
};

const MARGIN_X = 50; 
const MARGIN_Y = 50;
const INFLEXION_PADDING = 20;
const TITLE_HEIGHT = 50;

const pieGenerator = d3.pie<DataItem>().value((d) => d.value);
const arcGenerator = d3.arc();

const calculateDimensions = (width: number, height: number) => ({
  width,
  height,
  radius:
    Math.min(width - 2 * MARGIN_X, height - 2 * MARGIN_Y - TITLE_HEIGHT) / 2,
});

const PieChart = ({ data, title }: PieChartProps) => {
  const refDiv = useRef<HTMLDivElement>(null);
  const refG = useRef<SVGGElement>(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    radius: 0,
  });
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  const [hoveredData, setHoveredData] = useState<{
    data: DataItem;
    x: number;
    y: number;
  } | null>(null);

  const colorScale = useMemo(() => {
    const domain = data.map((d) => d.name);
    return d3.scaleOrdinal<string>().domain(domain).range(d3.schemePaired);
  }, [data]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect) {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
        resizeTimeout.current = setTimeout(() => {
          const { width, height } = entries[0].contentRect;
          setDimensions(calculateDimensions(width, height));
        }, 10);
      }
    });

    if (refDiv.current) {
      resizeObserver.observe(refDiv.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  const pie = useMemo(() => pieGenerator(data), [data]);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent, grp: PieArcDatum) => {
      if (refG.current) {
        refG.current.classList.add(styles.hasHighlight);
      }
      if (!refDiv.current) return;
      const containerRect = refDiv.current.getBoundingClientRect();
      const x = event.clientX - containerRect.left;
      const y = event.clientY - containerRect.top;
      setHoveredData({ data: grp.data, x: x, y: y });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    if (refG.current) {
      refG.current.classList.remove(styles.hasHighlight);
    }
    setHoveredData(null);
  }, []);

  const renderShapes = () =>
    pie.map((grp, i) => {
      const sliceInfo = {
        innerRadius: 0,
        outerRadius: dimensions.radius,
        startAngle: grp.startAngle,
        endAngle: grp.endAngle,
      };
      const centroid = arcGenerator.centroid(sliceInfo);
      const slicePath = arcGenerator(sliceInfo);

      const inflexionInfo = {
        innerRadius: dimensions.radius + INFLEXION_PADDING,
        outerRadius: dimensions.radius + INFLEXION_PADDING,
        startAngle: grp.startAngle,
        endAngle: grp.endAngle,
      };
      const inflexionPoint = arcGenerator.centroid(inflexionInfo);

      const isRightLabel = inflexionPoint[0] > 0;
      const labelPosX = inflexionPoint[0] + 50 * (isRightLabel ? 1 : -1);
      const textAnchor = isRightLabel ? "start" : "end";
      const label = `${grp.data.name} (${grp.data.value})`;

      return (
        <g
          key={i}
          className={styles.slice}
        >
          <path
            d={slicePath as string}
            fill={colorScale(grp.data.name)}
            style={{ cursor: "pointer" }}
            onMouseEnter={(e) => handleMouseEnter(e, grp)}
            onMouseLeave={handleMouseLeave}
          />

          
          
        </g>
      );
    });
  return (

    <div
      style={{
        width: "100%",
        height: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid #ddd",
        borderRadius: "8px",
        overflow: "hidden", 
        display: "flex", 
      }}
    >
      <div
        ref={refDiv}
        style={{ flex: 1, height: "100%", position: "relative" }}
        className={styles.grafico}
      >
        <svg
          width={dimensions.width}
          height={dimensions.height}
          style={{ display: "block" }}
        >
          <text
            x={dimensions.width / 2}
            y={TITLE_HEIGHT / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={16}
            fontWeight="bold"
          >
            {title}
          </text>
          <g
            transform={`translate(${dimensions.width / 2}, ${
              dimensions.height / 2 + TITLE_HEIGHT / 2
            })`}
            ref={refG}
            className={styles.container}
          >
            {dimensions.width > 0 && renderShapes()}
          </g>
        </svg>

        {hoveredData && (
          <div
            style={{
              position: "absolute",
              top: `${hoveredData.y}px`,
              left: `${hoveredData.x}px`,
              padding: "8px 12px",
              background: "rgba(0, 0, 0, 0.75)",
              color: "white",
              borderRadius: "4px",
              fontSize: "14px",
              pointerEvents: "none",
              transform: "translate(10px, -100%)",
              zIndex: 1000,
              transition: "top 0.1s linear, left 0.1s linear",
            }}
          >
            <div>{hoveredData.data.name}</div>
            <div>Valor: {hoveredData.data.value}</div>
          </div>
        )}
      </div>

      <div
        className={styles.legendContainer}
        style={{
          width: "200px",
          height: "100%",
          padding: `${TITLE_HEIGHT}px 10px 10px 10px`,
          overflowY: "auto",
          boxSizing: "border-box",
          backgroundColor: "#f9f9f9",
          borderLeft: "1px solid #eee", 
        }}
      >
        <h4 style={{ marginTop: 0 }}>Leyenda</h4> 
        {data.map((item) => (
          <div
            key={item.name}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                width: "16px",
                height: "16px",
                backgroundColor: colorScale(item.name),
                marginRight: "8px",
                border: "1px solid #777",
                flexShrink: 0,
              }}
            ></span>
            <span style={{ wordBreak: "break-word", fontSize: "14px" }}>
              {item.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PieChart;